// @ts-check
// Condições simples da automação (contrato 08, seção 3.1): linhas "campo, operador, valor" ligadas por E, gravadas
// como a árvore de filtro do contrato MVP. Árvores que a tela não sabe montar ficam intactas e só são exibidas.
import { html } from 'htm/preact';
import { Icon } from '../../ui/icon.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { CONDITION_FIELD_TYPES, findField, isMemberRelation, isWatchableField, pickFields, recordKeyOf } from './automation-labels.js';
import { VariablePicker } from './automation-variables.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {{ field: string, operator: string, value: any }} ConditionRow
 */

const RATING_VALUES = ['RATING_1', 'RATING_2', 'RATING_3', 'RATING_4', 'RATING_5'];
const LIST_OPERATORS = new Set(['in', 'notIn', 'containsAny']);
const VALUELESS_OPERATORS = new Set(['isEmpty', 'isNotEmpty']);

/** Valor de um input datetime-local (hora local) a partir de um ISO. @param {string} value */
function toLocalDateTimeInput(value) {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/** @param {FieldMetadata | undefined} field */
export function operatorsFor(field) {
  if (!field) return [];
  switch (field.type) {
    case 'SELECT':
      return ['eq', 'neq', 'in', 'notIn', 'isEmpty', 'isNotEmpty'];
    case 'MULTI_SELECT':
      return ['containsAny', 'isEmpty', 'isNotEmpty'];
    case 'RELATION':
      return isMemberRelation(field) ? ['eq', 'neq', 'isEmpty', 'isNotEmpty'] : ['isEmpty', 'isNotEmpty'];
    case 'TEXT':
      return ['eq', 'neq', 'contains', 'isEmpty', 'isNotEmpty'];
    case 'NUMBER':
    case 'RATING':
      return ['eq', 'neq', 'gt', 'lt', 'isEmpty', 'isNotEmpty'];
    case 'BOOLEAN':
      return ['eq'];
    case 'DATE_TIME':
    case 'DATE':
      return ['gt', 'lt', 'isEmpty', 'isNotEmpty'];
    default:
      return ['isEmpty', 'isNotEmpty'];
  }
}

/** @param {any} node @returns {ConditionRow | null} */
function nodeToRow(node) {
  if (node && typeof node === 'object' && 'not' in node) {
    const inner = node.not;
    return inner?.operator === 'in' && typeof inner.field === 'string' ? { field: inner.field, operator: 'notIn', value: inner.value ?? [] } : null;
  }
  if (!node || typeof node.field !== 'string' || typeof node.operator !== 'string' || node.subField) return null;
  if (node.operator === 'is') return { field: node.field, operator: node.value === 'NULL' ? 'isEmpty' : 'isNotEmpty', value: null };
  if (node.operator === 'ilike' && typeof node.value === 'string' && /^%.*%$/.test(node.value)) return { field: node.field, operator: 'contains', value: node.value.slice(1, -1) };
  if (['eq', 'neq', 'in', 'containsAny', 'gt', 'lt'].includes(node.operator)) return { field: node.field, operator: node.operator, value: node.value };
  return null;
}

/**
 * Árvore de filtro para linhas; null quando a árvore tem OU, aninhamento ou operadores que a tela não monta.
 * @param {any} conditions @returns {ConditionRow[] | null}
 */
export function parseConditions(conditions) {
  if (!conditions) return [];
  const nodes = Array.isArray(conditions.and) ? conditions.and : [conditions];
  const rows = nodes.map(nodeToRow);
  return rows.every(Boolean) ? /** @type {ConditionRow[]} */ (rows) : null;
}

/** @param {ConditionRow} row */
function rowToNode(row) {
  switch (row.operator) {
    case 'isEmpty':
      return { field: row.field, operator: 'is', value: 'NULL' };
    case 'isNotEmpty':
      return { field: row.field, operator: 'is', value: 'NOT_NULL' };
    case 'notIn':
      return { not: { field: row.field, operator: 'in', value: row.value ?? [] } };
    case 'contains':
      return { field: row.field, operator: 'ilike', value: `%${row.value ?? ''}%` };
    default:
      return { field: row.field, operator: row.operator, value: row.value };
  }
}

/** @param {ConditionRow[]} rows */
export function serializeConditions(rows) {
  const complete = rows.filter((row) => row.field && row.operator);
  return complete.length === 0 ? null : { and: complete.map(rowToNode) };
}

/**
 * @param {{ field: FieldMetadata, row: ConditionRow, members: any[], disabled: boolean, onChange: (value: any) => void,
 *   variableGroups?: import('./automation-variables.js').VariableGroup[] }} props
 */
function ConditionValue({ field, row, members, disabled, onChange, variableGroups }) {
  if (VALUELESS_OPERATORS.has(row.operator)) return html`<span></span>`;
  const options = field.type === 'RATING' ? RATING_VALUES.map((value) => ({ value, label: value.replace('RATING_', '★ ') })) : field.options ?? [];
  if (LIST_OPERATORS.has(row.operator)) {
    const selected = new Set(Array.isArray(row.value) ? row.value : []);
    return html`<div class="automation-chip-picker">
      ${options.map((option) => html`<button type="button" key=${option.value} class="automation-chip" aria-pressed=${selected.has(option.value) ? 'true' : 'false'} disabled=${disabled}
        onClick=${() => {
          const next = new Set(selected);
          if (next.has(option.value)) next.delete(option.value);
          else next.add(option.value);
          onChange([...next]);
        }}>${translateMetadataLabel(option.label)}</button>`)}
    </div>`;
  }
  if (field.type === 'SELECT' || field.type === 'RATING') {
    return html`<select class="text-input" value=${row.value ?? ''} disabled=${disabled} onChange=${(event) => onChange(event.currentTarget.value)}>
      <option value="">${t('automations.form.choose')}</option>
      ${options.map((option) => html`<option key=${option.value} value=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
    </select>`;
  }
  if (field.type === 'RELATION') {
    return html`<select class="text-input" value=${row.value ?? ''} disabled=${disabled} onChange=${(event) => onChange(event.currentTarget.value)}>
      <option value="">${t('automations.form.choose')}</option>
      ${members.map((member) => html`<option key=${member.id} value=${member.id}>${[member.name?.firstName, member.name?.lastName].filter(Boolean).join(' ')}</option>`)}
    </select>`;
  }
  if (field.type === 'BOOLEAN') {
    return html`<select class="text-input" value=${String(row.value ?? true)} disabled=${disabled} onChange=${(event) => onChange(event.currentTarget.value === 'true')}>
      <option value="true">${t('common.true')}</option><option value="false">${t('common.false')}</option>
    </select>`;
  }
  if (field.type === 'DATE_TIME') {
    const local = row.value ? toLocalDateTimeInput(row.value) : '';
    return html`<input class="text-input" type="datetime-local" value=${local} disabled=${disabled} onInput=${(event) => onChange(event.currentTarget.value ? new Date(event.currentTarget.value).toISOString() : null)} />`;
  }
  if (field.type === 'DATE') {
    return html`<input class="text-input" type="date" value=${row.value ?? ''} disabled=${disabled} onInput=${(event) => onChange(event.currentTarget.value || null)} />`;
  }
  const isNumber = field.type === 'NUMBER';
  if (variableGroups && variableGroups.length > 0) {
    // valor com variável (ex.: o e-mail que chegou no webhook): texto livre com o seletor ao lado
    return html`<div class="automation-variable-input">
      <input class="text-input" type="text" value=${row.value ?? ''} disabled=${disabled} aria-label=${t('automations.form.value')}
        onInput=${(event) => {
          const text = event.currentTarget.value;
          onChange(isNumber && text !== '' && !text.includes('{{') && !Number.isNaN(Number(text)) ? Number(text) : text);
        }} />
      ${disabled ? null : html`<${VariablePicker} groups=${variableGroups} onSelect=${(item) => onChange(`${row.value ?? ''}${item.token}`)} />`}
    </div>`;
  }
  return html`<input class="text-input" type=${isNumber ? 'number' : 'text'} value=${row.value ?? ''} disabled=${disabled}
    onInput=${(event) => onChange(isNumber ? (event.currentTarget.value === '' ? null : Number(event.currentTarget.value)) : event.currentTarget.value)} />`;
}

// variável comparada como texto ou número (corpo do webhook, saída de um passo)
const VARIABLE_FIELD = /** @type {FieldMetadata} */ (/** @type {unknown} */ ({ id: 'variable', name: 'variable', label: 'variable', type: 'TEXT' }));
const VARIABLE_OPERATORS = ['eq', 'neq', 'contains', 'gt', 'lt', 'isEmpty', 'isNotEmpty'];
/** @param {string} field */
const isVariablePath = (field) => /^(trigger|steps)\./.test(field ?? '');

/**
 * @param {{ objectMetadata: ObjectMetadata | undefined, rows: ConditionRow[] | null, rawConditions: any, members: any[], disabled: boolean,
 *   onChange: (rows: ConditionRow[]) => void, variableGroups?: import('./automation-variables.js').VariableGroup[], allowVariableFields?: boolean }} props
 *   variableGroups: valores de texto aceitam variáveis; allowVariableFields: o campo da condição pode ser uma variável
 */
export function ConditionsEditor({ objectMetadata, rows, rawConditions, members, disabled, onChange, variableGroups, allowVariableFields = false }) {
  if (rows === null) {
    return html`<div class="automation-advanced-note">
      <${Icon} name="IconInfoCircle" size=${14} /><span>${t('automations.form.advancedConditions')}</span>
      <code>${JSON.stringify(rawConditions)}</code>
      ${disabled ? null : html`<${Button} title=${t('automations.form.clearConditions')} variant="secondary" size="small" onClick=${() => onChange([])} />`}
    </div>`;
  }
  const fields = pickFields(objectMetadata, (field) => isWatchableField(field) && CONDITION_FIELD_TYPES.has(field.type));
  const update = (/** @type {number} */ index, /** @type {Partial<ConditionRow>} */ patch) => onChange(rows.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  return html`<div class="automation-conditions">
    ${rows.length === 0 ? html`<p class="automation-muted">${t('automations.form.noConditions')}</p>` : null}
    ${rows.map((row, index) => {
      const isVariable = isVariablePath(row.field);
      const field = isVariable ? VARIABLE_FIELD : findField(objectMetadata, row.field);
      const operators = isVariable ? VARIABLE_OPERATORS : operatorsFor(field);
      const variableOptions = allowVariableFields ? (variableGroups ?? []) : [];
      const knownVariable = variableOptions.some((group) => group.items.some((item) => item.path === row.field));
      return html`<div class="automation-condition-row" key=${index}>
        <span class="automation-condition-joiner">${index === 0 ? t('automations.form.when') : t('automations.form.and')}</span>
        <select class="text-input" value=${isVariable ? row.field : field ? recordKeyOf(field) : ''} disabled=${disabled} aria-label=${t('automations.form.field')}
          onChange=${(event) => {
            const value = event.currentTarget.value;
            const next = isVariablePath(value) ? VARIABLE_FIELD : findField(objectMetadata, value);
            update(index, { field: value, operator: (isVariablePath(value) ? VARIABLE_OPERATORS : operatorsFor(next))[0] ?? 'isEmpty', value: null });
          }}>
          <option value="">${t('automations.form.chooseField')}</option>
          ${fields.length > 0 && variableOptions.length > 0 ? html`<optgroup label=${objectMetadata ? translateMetadataLabel(objectMetadata.labelSingular) : ''}>
              ${fields.map((candidate) => html`<option key=${candidate.id} value=${recordKeyOf(candidate)}>${translateMetadataLabel(candidate.label)}</option>`)}
            </optgroup>`
            : fields.map((candidate) => html`<option key=${candidate.id} value=${recordKeyOf(candidate)}>${translateMetadataLabel(candidate.label)}</option>`)}
          ${variableOptions.filter((group) => group.key !== 'record' && group.key !== 'contact' && group.key !== 'institution').map((group) => html`<optgroup key=${group.key} label=${group.label}>
            ${group.items.map((item) => html`<option key=${item.path} value=${item.path}>${item.label}</option>`)}
          </optgroup>`)}
          ${isVariable && !knownVariable ? html`<option value=${row.field}>${row.field}</option>` : null}
        </select>
        <select class="text-input" value=${row.operator} disabled=${disabled || !field} aria-label=${t('automations.form.operator')}
          onChange=${(event) => update(index, { operator: event.currentTarget.value, value: LIST_OPERATORS.has(event.currentTarget.value) ? [] : null })}>
          ${operators.map((operator) => html`<option key=${operator} value=${operator}>${t(`automations.operators.${operator}`)}</option>`)}
        </select>
        ${field ? html`<${ConditionValue} field=${field} row=${row} members=${members} disabled=${disabled} variableGroups=${variableGroups} onChange=${(value) => update(index, { value })} />` : html`<span></span>`}
        ${disabled ? null : html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('automations.form.removeCondition')} onClick=${() => onChange(rows.filter((_row, position) => position !== index))} />`}
      </div>`;
    })}
    ${disabled ? null : html`<div><${Button} title=${t('automations.form.addCondition')} icon="IconPlus" variant="secondary" size="small"
      onClick=${() => onChange([...rows, { field: '', operator: 'eq', value: null }])} /></div>`}
  </div>`;
}
